import React, { useMemo, useState } from 'react';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { ITableColumn } from 'src/components/Table/types';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { getServiceRequestById } from 'src/services/client';
import { createPerPageServiceRequestTableColumns } from 'src/pages/ServiceRequests/utils/perPageServiceRequestTableColumns';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { isExternalPartyRole } from 'src/helpers/permissions';

const SKELETON_ROW_COUNT: number = 13;

export interface PerPageServiceRequestLightboxProps {
  isOpen: boolean;
  images: string[];
  initialIndex: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export interface UsePerPageServiceRequestTableStateParams {
  visibleServiceRequests: any[];
  selectedIds: string[];
  allVisibleSelected: boolean;
  toggleSelection: (id: string) => void;
  toggleSelectionBatch: (ids: string[]) => void;
  toggleSelectAllVisible: () => void;
  navigateTo: string;
  navLabel: string;
  onDeleteGroup?: (groupId: string) => void;
  onRemoveFromGroup?: (serviceRequestId: string, groupId: string) => void;
}

export interface UsePerPageServiceRequestTableStateReturn {
  columns: ITableColumn[];
  skeletonColumns: ITableColumn[];
  skeletonData: any[];
  expandedGroups: any[];
  lightboxProps: PerPageServiceRequestLightboxProps;
}

export const usePerPageServiceRequestTableState = ({
  visibleServiceRequests,
  selectedIds,
  allVisibleSelected,
  toggleSelection,
  toggleSelectionBatch,
  toggleSelectAllVisible,
  navigateTo,
  navLabel,
  onDeleteGroup = () => undefined,
  onRemoveFromGroup = () => undefined,
}: UsePerPageServiceRequestTableStateParams): UsePerPageServiceRequestTableStateReturn => {
  const { branding } = useBranding();
  const user = useSelector((state: any) => selectAuthenticatedUser(state));

  // Internal-only columns (internal/external Visibility + Days Open) are hidden from
  // external parties (home owners, realtors, occupants); every internal/staff role sees them.
  const canSeeInternalColumns: boolean = !isExternalPartyRole(user);

  const [expandedGroups, setExpandedGroups] = useState<any[]>([]);
  const [lightboxImages, setLightboxImages] = useState<string[]>([]);
  const [showLightbox, setShowLightbox] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);

  const hasTarionNumbers: boolean = useMemo(() => {
    return visibleServiceRequests.some((serviceRequest: any) => {
      if (serviceRequest.isGroup && serviceRequest.children) {
        return serviceRequest.children.some(
          (child: any) => child.tarionItemNumber !== null
        );
      }
      return serviceRequest.tarionItemNumber !== null;
    });
  }, [visibleServiceRequests]);

  const openLightboxForServiceRequest = async (serviceRequestId: string) => {
    const response: any = await getServiceRequestById(serviceRequestId);

    if (response.ok || response.status === 200) {
      const data = await response.json();
      const attachments = JSON.parse(data.attachment || '[]');

      if (attachments.length > 0) {
        setLightboxImages(attachments);
        setLightboxIndex(0);
        setShowLightbox(true);
      }
    }
  };

  const columns: ITableColumn[] = useMemo(
    () =>
      createPerPageServiceRequestTableColumns({
        allSelected: allVisibleSelected,
        onSelectAll: toggleSelectAllVisible,
        expandedGroups,
        setExpandedGroups,
        selectedServiceRequests: selectedIds,
        onSelectGroup: (ids: string[]) => toggleSelectionBatch(ids),
        onDeleteGroup,
        onSelectServiceRequest: (id: string) => toggleSelection(id),
        onRemoveFromGroup,
        branding,
        hasTarionNumbers,
        onClickAttachment: openLightboxForServiceRequest,
        navigateTo,
        navLabel,
        canSeeInternalColumns,
      }),
    [
      allVisibleSelected,
      toggleSelectAllVisible,
      selectedIds,
      toggleSelection,
      toggleSelectionBatch,
      expandedGroups,
      branding,
      hasTarionNumbers,
      navigateTo,
      navLabel,
      onDeleteGroup,
      onRemoveFromGroup,
      canSeeInternalColumns,
    ]
  );

  const skeletonColumns: ITableColumn[] = useMemo(
    () =>
      columns.map((column: ITableColumn) => ({
        ...column,
        cell: () => <Skeleton variant="text" width="70%" />,
      })),
    [columns]
  );

  const skeletonData: any[] = useMemo(
    () =>
      Array.from({ length: SKELETON_ROW_COUNT }, (_, index: number) => ({
        id: `service-request-skeleton-${index}`,
      })),
    []
  );

  const lightboxProps: PerPageServiceRequestLightboxProps = {
    isOpen: showLightbox && lightboxImages.length > 0,
    images: lightboxImages,
    initialIndex: lightboxIndex,
    onClose: () => {
      setShowLightbox(false);
      setLightboxIndex(0);
      setLightboxImages([]);
    },
    onIndexChange: (index: number) => setLightboxIndex(index),
  };

  return {
    columns,
    skeletonColumns,
    skeletonData,
    expandedGroups,
    lightboxProps,
  };
};
